/**
 * Position Mapping Utilities for Incremental Parking
 *
 * Converts aircraft positions → spot IDs using point-in-polygon tests.
 * Used to treat existing placements as locked during incremental placement.
 */

import { Shape } from "../../Settings/RampsSettings/RampEditor/RampCanvas/RampCanvas";
import { Spot } from "./types";
import { orInto, makeEmptyMask } from "./bitmap";

/**
 * Check if a point is inside a polygon using ray casting algorithm
 * (Copied from zoneHelpers.ts for reusability)
 */
function isPointInPolygon(point: { x: number; y: number }, polygon: { x: number; y: number }[]): boolean {
  if (!polygon || polygon.length < 3) {
    return false;
  }

  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x;
    const yi = polygon[i].y;
    const xj = polygon[j].x;
    const yj = polygon[j].y;

    const intersect =
      yi > point.y !== yj > point.y &&
      point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;

    if (intersect) {
      inside = !inside;
    }
  }

  return inside;
}

/**
 * Find which spot contains the given position
 * Uses ray-casting algorithm for point-in-polygon test
 *
 * IMPORTANT: This function handles coordinate system translation.
 * - Database positions use bottom-left origin (Y increases upward)
 * - Ramp shapes use top-left origin (Y increases downward)
 * - We Y-flip shapes before testing to match coordinate systems
 *
 * @param position - Aircraft position (x, y coordinates) from database (bottom-left origin)
 * @param shapes - Ramp shapes (includes spot geometries) in top-left origin
 * @param rampDepth - Ramp depth in feet (needed for Y-axis flip)
 * @returns Spot ID if found, null if position not in any spot or unplaced
 *
 * @example
 * ```typescript
 * const spotId = findSpotForPosition({ x: 100, y: 200 }, ramp.shapes, ramp.depth);
 * if (spotId) {
 *   console.log(`Aircraft is in spot ${spotId}`);
 * }
 * ```
 */
export function findSpotForPosition(
  position: { x: number | null; y: number | null },
  shapes: Shape[],
  rampDepth: number
): string | null {
  // Handle unplaced aircraft
  if (position.x === null || position.y === null) {
    return null;
  }

  const point = { x: position.x, y: position.y };

  // Find spot shapes
  const spotShapes = shapes.filter(s => s.type === "Spot");

  for (const shape of spotShapes) {
    if (!shape.points || shape.points.length === 0) {
      continue;
    }

    // Y-flip shape points to match database coordinate system (bottom-left origin)
    // This is the same transformation used in AutoPark (runSnapshotParkingOnRamp, etc.)
    const flippedPoints = shape.points.map(p => ({
      x: p.x,
      y: rampDepth - p.y
    }));

    const isInside = isPointInPolygon(point, flippedPoints);

    if (isInside) {
      return shape.id;
    }
  }

  return null;
}

/**
 * Mark spots as occupied for existing aircraft placements
 * Modifies spot.occupancy bitmaps in place
 *
 * Used for incremental placement to treat existing aircraft as locked.
 * Converts aircraft positions → spot IDs → bitmap occupancy.
 *
 * @param placedAircraft - Aircraft with positions already set
 * @param spots - Spot objects (bitmaps modified in place)
 * @param shapes - Ramp shapes for position→spot mapping
 * @param rampDepth - Ramp depth in feet (needed for Y-axis flip in position mapping)
 * @param startBucket - Time bucket to mark as occupied (default: 0 for snapshot)
 * @param endBucket - End bucket (default: 1 for snapshot)
 *
 * @example
 * ```typescript
 * const existingAircraft = stack.tenants.filter(t =>
 *   t.position.x !== null && t.id !== selectedAircraftId
 * );
 * markExistingSpotsOccupied(existingAircraft, spots, ramp.shapes, ramp.depth);
 * ```
 */
export function markExistingSpotsOccupied(
  placedAircraft: Array<{ id: string; position: { x: number | null; y: number | null } }>,
  spots: Spot[],
  shapes: Shape[],
  rampDepth: number,
  startBucket: number = 0,
  endBucket: number = 1
): void {
  for (const aircraft of placedAircraft) {
    const spotId = findSpotForPosition(aircraft.position, shapes, rampDepth);

    if (!spotId) {
      // Aircraft is unplaced or position not in any spot - skip
      continue;
    }

    const spot = spots.find(s => s.id === spotId);
    if (!spot) {
      console.warn(`[markExistingSpotsOccupied] Spot ${spotId} not found in spots array for aircraft ${aircraft.id}`);
      continue;
    }

    // Mark spot as occupied using bitmap operations
    const WORD_COUNT = spot.occupancy.length;
    const mask = makeEmptyMask(WORD_COUNT * 64);

    for (let bucket = startBucket; bucket < endBucket; bucket++) {
      const wordIdx = Math.floor(bucket / 64);
      const bitIdx = BigInt(bucket % 64);
      if (wordIdx < WORD_COUNT) {
        mask[wordIdx] |= (1n << bitIdx);
      }
    }

    orInto(spot.occupancy, mask);
  }
}
